import { get } from 'lodash';
import Vue from 'vue';
import Pusher from 'pusher-js';

import cookies from '@/utils/cookies';
import {
  fetchFoodCourtDetailsRequest,
  fetchFoodCourtOrdersRequest,
  fetchFoodCourtRestaurantsRequest,
  fetchFoodCourtTranslationsRequest,
  fetchRestaurantSessionRequest,
  updateFoodCourtSessionLanguageRequest,
} from './api';
import router from '../../../router';
import cartModule from './cart';

const initialState = {
  foodCourtSession: cookies.getCookie('foodCourtSession') || null,
  foodCourtHash: cookies.getCookie('foodCourtHash') || null,
  restaurantSessionMap:
    JSON.parse(cookies.getCookie('restaurantSessionMap')) || {},
  foodCourtUserPhoneNumber: {
    countryCallingCode: cookies.getCookie('foodCourtUserPhoneNumberCountryCode') || null,
    nationalNumber: cookies.getCookie('foodCourtUserPhoneNumberNational') || null,
  },
  foodCourt: {},
  language: null,
  restaurants: [],
  orders: [],
  fetchedLanguages: [],
  subscribedToPusherChannels: null,
  foodCourtStatus: 'idle',
  restaurantsStatus: 'idle',
  foodCourtOrdersStatus: 'idle',
};

// Food court cookies TTL in hours
const TTL = 6;

// Loop guard for linkCurrentRestaurantSessionToFoodCourt: remember (per tab)
// which session we already reloaded onto so a misbehaving backend cannot bounce
// the user between sessions forever.
const REDIRECTED_SESSION_KEY = 'foodCourtLinkedSessionRedirect';
const getRedirectedSession = () => {
  try {
    return window.sessionStorage.getItem(REDIRECTED_SESSION_KEY);
  } catch (e) {
    return null;
  }
};
const setRedirectedSession = session => {
  try {
    window.sessionStorage.setItem(REDIRECTED_SESSION_KEY, session);
  } catch (e) {
    // ignore storage failures
  }
};

export default {
  state: initialState,
  mutations: {
    setFoodCourt(state, foodCourt) {
      state.foodCourt = foodCourt;
    },
    setFoodCourtSession(state, foodCourtSession) {
      state.foodCourtSession = foodCourtSession;
    },
    setLanguage(state, language) {
      state.language = language;
    },
    setRestaurants(state, restaurants) {
      state.restaurants = restaurants;
    },
    setFoodCourtStatus(state, foodCourtStatus) {
      state.foodCourtStatus = foodCourtStatus;
    },
    setRestaurantsStatus(state, restaurantsStatus) {
      state.restaurantsStatus = restaurantsStatus;
    },
    setFoodCourtOrdersStatus(state, foodCourtOrdersStatus) {
      state.foodCourtOrdersStatus = foodCourtOrdersStatus;
    },
    setOrders(state, orders) {
      state.orders = orders;
    },
    setRestaurantSession(state, { restaurantId, session }) {
      Vue.set(state.restaurantSessionMap, restaurantId, session);
    },
    setFoodCourtHash(state, hash) {
      state.foodCourtHash = hash;
    },
    addFetchedLanguage(state, language) {
      state.fetchedLanguages.push(language);
    },
    setSubscribedToPusherChannels(state, id) {
      state.subscribedToPusherChannels = id;
    },
    updateRestaurantStatus(
      state,
      { restaurantId, status, rushModeWaitMinutes }
    ) {
      const restaurant = state.restaurants.find(
        restaurant => restaurant.id === restaurantId
      );
      if (restaurant) {
        restaurant.order_status = status;
        restaurant.rush_mode_wait_minutes = rushModeWaitMinutes;
      }
    },
    updateRestaurantIsCurrentlyWorkingHours(state, { restaurantId, status }) {
      const restaurant = state.restaurants.find(
        restaurant => restaurant.id === restaurantId
      );
      if (restaurant) {
        restaurant.is_currently_working_hours = status;
      }
    },
    setFoodCourtUserPhoneNumber(state, phoneData) {
      state.foodCourtUserPhoneNumber = phoneData;
    },
  },
  actions: {
    setFoodCourtHash({ commit, dispatch }, { hash }) {
      commit('setFoodCourtHash', hash);
      dispatch('saveFoodCourtHashCookie', { hash });
    },
    async fetchFoodCourtDetails({ commit, getters, dispatch }, { hash }) {
      // Do not show food court selection for Kiosk (self-service table or a
      // kiosk device flagged by the native app)
      if (getters.tableTypeIsSelfService || getters.isKiosk) {
        return;
      }

      const foodCourtDataAlreadyFetched = Object.keys(
        getters.getFoodCourtDetails
      ).length;

      if (foodCourtDataAlreadyFetched) {
        return;
      }

      try {
        commit('setFoodCourtStatus', 'loading');

        const foodCourtSession = getters.getFoodCourtSession;
        const foodCourtHash = getters.getFoodCourtHash;

        const { data } = await fetchFoodCourtDetailsRequest({
          hash,
          foodCourtSession,
        });

        if (!foodCourtHash || foodCourtHash !== hash) {
          commit('setFoodCourtHash', hash);

          // Redirect to food court page if user shared a restaurant link
          // router.replace({
          //   name: 'FoodCourt',
          //   params: { foodCourtHash: hash },
          // });
        }

        // The backend may mint a new food court session (none stored yet, or
        // the stored one expired). Always adopt the one it returns.
        const foodCourtSessionChanged =
          !!data.food_court_session &&
          data.food_court_session !== foodCourtSession;

        if (foodCourtSessionChanged) {
          commit('setFoodCourtSession', data.food_court_session);
          dispatch('saveFoodCourtCookies');
        }

        commit('setFoodCourt', data.food_court);
        commit('setLanguage', data.language);

        await dispatch('fetchFoodCourtOrders');
        dispatch('fetchFoodCourtRestaurants', { hash });

        // If the user is already inside one of this food court's restaurants
        // (direct QR/link entry, or the food court session just rotated), make
        // sure the restaurant session they add items to is one that belongs to
        // this food court session, otherwise get-carts never includes it and the
        // badge shows 0. This may reload onto a linked session.
        const redirected = await dispatch(
          'linkCurrentRestaurantSessionToFoodCourt',
          { force: foodCourtSessionChanged }
        );
        if (redirected) return;

        dispatch('fetchFoodCourtCarts');

        // Set translations
        const translations = (await import(`../../../i18n/${data.language}.json`))
          .default;
        Vue.i18n.add(data.language, translations);
        Vue.i18n.set(data.language);
        dispatch('fetchFoodCourtTranslations', {
          hash,
          languageCode: data.language,
        });

        commit('setFoodCourtStatus', 'idle');
      } catch (error) {
        console.error(error);
        commit('setFoodCourtStatus', 'idle');
      }
    },
    async fetchFoodCourtRestaurants({ commit, getters, rootState }, { hash }) {
      const foodCourtRestaurantsAlreadyFetched =
        getters.getFoodCourtRestaurants.length;

      if (foodCourtRestaurantsAlreadyFetched) {
        return;
      }

      try {
        commit('setRestaurantsStatus', 'loading');
        const { data } = await fetchFoodCourtRestaurantsRequest({ hash });
        commit('setRestaurants', data.data);
        commit('setRestaurantsStatus', 'idle');

        // HACK for locale and currency settings
        const restaurants = data.data;
        rootState.locale = restaurants.length ? restaurants[0].locale : null;
        rootState.currency = restaurants.length
          ? restaurants[0].currency
          : null;
      } catch (error) {
        console.error(error);
        commit('setRestaurantsStatus', 'idle');
      }
    },
    async fetchRestaurantSession(
      { getters, commit, dispatch },
      { restaurantId, onSuccess }
    ) {
      const foodCourtSession = getters.getFoodCourtSession;
      const existingSession = getters.getRestaurantSession(restaurantId);

      try {
        // Always go through the backend, even when a session is cached: the
        // food court session and the restaurant session map live in separate
        // cookies and can drift apart, so the cached restaurant session must be
        // re-linked to the current food court session (or replaced if it is no
        // longer valid). Otherwise items added to it never show up in get-carts.
        const { data } = await fetchRestaurantSessionRequest({
          restaurantId,
          foodCourtSession,
          restaurantSession: existingSession,
        });
        const session = data.session || existingSession;

        commit('setRestaurantSession', { restaurantId, session });
        dispatch('saveFoodCourtCookies');
        onSuccess(session);
      } catch (error) {
        console.error(error);
        // Keep the user moving with the cached session if the link call failed
        if (existingSession) {
          onSuccess(existingSession);
        }
      }
    },
    // Ensures the restaurant session the user is browsing belongs to the current
    // food court session. Resolves to true when it had to reload the page onto a
    // different (linked) session, false otherwise.
    async linkCurrentRestaurantSessionToFoodCourt(
      { getters, rootState, commit, dispatch },
      { force = false } = {}
    ) {
      const foodCourtSession = getters.getFoodCourtSession;
      const restaurantSession = rootState.session;
      const restaurantId = rootState.restaurant.id;
      const restaurantFoodCourtHash = getters.restaurantFoodCourtHash;
      const foodCourtHash = getters.getFoodCourtHash;

      // Kiosks never take part in food court sessions: no linking and, above
      // all, no page reload on a kiosk device.
      if (
        getters.tableTypeIsSelfService ||
        getters.isKiosk ||
        getters.isPreview ||
        !foodCourtSession ||
        !restaurantSession ||
        !restaurantId ||
        !restaurantFoodCourtHash ||
        (foodCourtHash && foodCourtHash !== restaurantFoodCourtHash)
      ) {
        return false;
      }

      const alreadyLinked =
        getters.getRestaurantSession(restaurantId) === restaurantSession;

      if (alreadyLinked && !force) {
        return false;
      }

      try {
        const { data } = await fetchRestaurantSessionRequest({
          restaurantId,
          foodCourtSession,
          restaurantSession,
        });
        const linkedSession = data.session || restaurantSession;

        commit('setRestaurantSession', { restaurantId, session: linkedSession });
        dispatch('saveFoodCourtCookies');

        if (linkedSession === restaurantSession) {
          return false;
        }

        // The backend does not attach an existing restaurant session to another
        // food court session; it hands out a new, linked one instead. Staying on
        // the old session would keep every added item invisible to the food
        // court cart, so move the user onto the linked session. A full reload is
        // used because the Menu view only initialises itself on mount.
        if (getRedirectedSession() === linkedSession) {
          console.warn('Already reloaded onto linked session once, not looping');
          return false;
        }
        setRedirectedSession(linkedSession);

        const { href } = router.resolve({
          name: 'Menu',
          params: { session: linkedSession },
          query: router.currentRoute.query,
        });
        window.location.replace(href);
        return true;
      } catch (error) {
        console.error(
          'Failed to link restaurant session to food court session:',
          error
        );
        return false;
      }
    },
    async fetchFoodCourtOrders({ getters, commit }) {
      try {
        commit('setFoodCourtOrdersStatus', 'loading');
        const session = getters.getFoodCourtSession;
        const { data } = await fetchFoodCourtOrdersRequest({
          foodCourtSession: session,
        });
        commit('setOrders', data.data);
        commit('setFoodCourtOrdersStatus', 'idle');
      } catch (error) {
        console.error(error);
      }
    },
    async fetchFoodCourtTranslations(
      { commit, getters },
      { hash, languageCode }
    ) {
      const languageAlreadyFetched = getters.getFetchedLanguageByCode(
        languageCode
      );

      if (languageAlreadyFetched) {
        return;
      }

      try {
        const { data } = await fetchFoodCourtTranslationsRequest({
          hash,
          languageCode,
        });
        Vue.i18n.add(languageCode, data);
        commit('addFetchedLanguage', languageCode);
      } catch (error) {
        console.error(error);
      }
    },
    // The food court session and the restaurant session map must expire
    // together. If only the food court session cookie lapses, a new food court
    // session is minted while the cached restaurant sessions still point at the
    // old one, and their carts vanish from get-carts.
    saveFoodCourtCookies({ state }) {
      if (state.foodCourtSession) {
        cookies.setCookie({
          name: 'foodCourtSession',
          value: state.foodCourtSession,
          hours: TTL,
        });
      }
      cookies.setCookie({
        name: 'restaurantSessionMap',
        value: JSON.stringify(state.restaurantSessionMap),
        hours: TTL,
      });
    },
    saveRestaurantSessionMapCookie({ dispatch }) {
      dispatch('saveFoodCourtCookies');
    },
    saveFoodCourtSessionCookie({ dispatch }) {
      dispatch('saveFoodCourtCookies');
    },
    saveFoodCourtHashCookie(_, { hash }) {
      cookies.setCookie({ name: 'foodCourtHash', value: hash, hours: TTL });
    },
    subscribeToFoodCourtChannels({ getters, dispatch, commit }) {
      const foodCourtSession = getters.getFoodCourtSession;
      const foodCourtHash = getters.getFoodCourtHash;
      const foodCourtId = getters.getFoodCourtId;
      const alreadySubscribedSession = getters.getIsSubscribedToPusherChannels;
      const isAlreadySubscribed = alreadySubscribedSession === foodCourtSession;

      if (isAlreadySubscribed || !foodCourtSession || !foodCourtId) {
        return;
      }

      Pusher.logToConsole = process.env.VUE_APP_ENVIRONMENT !== 'production';
      const pusher = new Pusher(process.env.VUE_APP_PUSHER_APP_KEY, {
        cluster: 'eu',
      });

      const foodCourtOrdersChanel = pusher.subscribe(
        `food-court-session_${foodCourtSession}`
      );
      const foodCourtChanel = pusher.subscribe(`food-court_${foodCourtId}`);

      commit('setSubscribedToPusherChannels', foodCourtSession);

      const openOrder = ({ unique_order_number }) => {
        const foodCourtHash = getters.getFoodCourtHash;
        const order = getters.getFoodCourtOrderOrderNumber(unique_order_number);
        const paymentUuid = get(
          order,
          'payments[0]composite_payment.uuid',
          null
        );

        // Do not proceed if payment UUID is missing
        if (!paymentUuid) {
          return;
        }

        router.push({
          name: 'ViewFoodCourtMultiMerchantOrder',
          params: { foodCourtHash },
          query: {
            paymentUuid,
          },
        });
      };

      const showToastNotification = ({
        title,
        description,
        action,
        restaurantName,
      }) => {
        const toastTitle = restaurantName
          ? `${restaurantName}. ${title}`
          : title;
        dispatch('showToastInstantly', {
          title: toastTitle,
          description,
          action,
        });
      };

      const handleOrderSeenByWaiter = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate('components.toast.orderSeenTitle'),
          description: Vue.i18n.translate(
            'components.toast.orderSeenDescription'
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };
      const handleOrderFailed = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate('components.toast.orderFailedTitle'),
          description: Vue.i18n.translate(
            'components.toast.orderFailedDescription'
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };
      const handleOrderAccepted = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate('components.toast.orderAcceptedTitle'),
          description: Vue.i18n.translate(
            'components.toast.orderAcceptedDescription',
            { restaurantName: data.restaurant_name }
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };
      const handleOrderItemDelivered = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate('components.toast.orderItemDeliveredTitle'),
          description: Vue.i18n.translate(
            'components.toast.orderItemDeliveredDescription'
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };
      const handleOrderFinished = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate('components.toast.orderFinishedTitle'),
          description: Vue.i18n.translate(
            'components.toast.orderFinishedDescription'
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };
      const handleWaitTimeProlonged = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate(
            'components.toast.orderWaitTimeProlongedTitle'
          ),
          description: Vue.i18n.translate(
            'components.toast.orderWaitTimeProlongedDescription'
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };
      const handleOrderRejected = data => {
        dispatch('fetchFoodCourtOrders');
        showToastNotification({
          title: Vue.i18n.translate('components.toast.orderRejectedTitle'),
          description: Vue.i18n.translate(
            'components.toast.orderRejectedDescription'
          ),
          restaurantName: data.restaurant_name,
          action: () => openOrder(data),
        });
      };

      const handleRushModeEnabled = data => {
        const time = data.rush_mode_wait_minutes;
        const restaurantId = data.restaurant_id;
        const showRushModeToast = getters.getShouldShowRushModeToast(
          data.restaurant_id
        );

        commit('updateRestaurantStatus', {
          restaurantId,
          status: 'rush_mode',
          rushModeWaitMinutes: time,
        });

        dispatch('fetchFoodCourtRestaurants', { hash: foodCourtHash });

        if (!showRushModeToast) {
          return;
        }

        showToastNotification({
          title: Vue.i18n.translate('components.toast.rushMode.title'),
          description: Vue.i18n.translate(
            'components.toast.rushMode.description',
            { time }
          ),
          restaurantName: data.restaurant_name,
        });
      };

      const handleRushModeDisabled = data => {
        const restaurantId = data.restaurant_id;

        commit('updateRestaurantStatus', {
          restaurantId,
          status: 'open',
          rushModeWaitMinutes: null,
        });

        dispatch('fetchFoodCourtRestaurants', { hash: foodCourtHash });
      };

      const handleOrderCreated = () => dispatch('fetchFoodCourtOrders');

      const handleRestaurantClosed = data => {
        const restaurantId = data.restaurant_id;

        commit('updateRestaurantStatus', {
          restaurantId,
          status: 'temporarily_disabled',
          rushModeWaitMinutes: null,
        });
      };

      const handleRestaurantOpened = data => {
        const restaurantId = data.restaurant_id;

        commit('updateRestaurantStatus', {
          restaurantId,
          status: 'open',
          rushModeWaitMinutes: null,
        });
      };

      const handleWorkingHoursUpdated = data => {
        const isWorkingHours = data.is_currently_working_hours;
        const restaurantId = data.restaurant_id;

        commit('updateRestaurantIsCurrentlyWorkingHours', {
          restaurantId,
          status: isWorkingHours,
        });
      };

      const foodCourtOrderEvents = [
        { event: 'order-seen-by-waiter', handler: handleOrderSeenByWaiter },
        { event: 'order-failed', handler: handleOrderFailed },
        { event: 'order-accepted', handler: handleOrderAccepted },
        { event: 'order-item-delivered', handler: handleOrderItemDelivered },
        { event: 'order-finished', handler: handleOrderFinished },
        {
          event: 'order-wait-time-prolonged',
          handler: handleWaitTimeProlonged,
        },
        { event: 'order-rejected', handler: handleOrderRejected },
        { event: 'order-created', handler: handleOrderCreated },
      ];

      const foodCourtEvents = [
        { event: 'rush-mode-activated', handler: handleRushModeEnabled },
        { event: 'rush-mode-deactivated', handler: handleRushModeDisabled },
        { event: 'restaurant-closed', handler: handleRestaurantClosed },
        { event: 'restaurant-opened', handler: handleRestaurantOpened },
        {
          event: 'is-currently-working-hours-updated',
          handler: handleWorkingHoursUpdated,
        },
      ];

      // Subscribe to all food court order events
      foodCourtOrderEvents.forEach(({ event, handler }) => {
        foodCourtOrdersChanel.bind(event, handler);
      });

      // Subscribe to all food court events
      foodCourtEvents.forEach(({ event, handler }) => {
        foodCourtChanel.bind(event, handler);
      });
    },
    updateFoodCourtSessionLanguage({ getters, commit }, { languageCode }) {
      const foodCourtSession = getters.getFoodCourtSession;

      if (!foodCourtSession) {
        return;
      }

      commit('setLanguage', languageCode);
      updateFoodCourtSessionLanguageRequest({
        foodCourtSession,
        languageCode,
      });
    },
    async fetchMultiMerchantOrderDetails({ commit }, { paymentUuid }) {
      try {
        const response = await Vue.axios.get(
          `/payments/payment-by-uuid/${paymentUuid}`
        );

        // set the order that are associated with this payment and other will be loaded with the full response
        commit('setOrders', response.data.data.orders);

        return response.data.data;
      } catch (error) {
        console.error('Error fetching multi-merchant order details:', error);
        throw error;
      }
    },
    async assignPhoneNumberToRestaurantSessions(
      { dispatch },
      { countryCallingCode, nationalNumber, sessions }
    ) {
      try {
        const data = await Promise.all(
          sessions.map(session =>
            Vue.axios.post(`/session/${session}/add-phone-number`, {
              countryCallingCode,
              nationalNumber,
            })
          )
        );
        dispatch(
          'setFoodCourtUserPhoneNumber',
          { countryCallingCode, nationalNumber }
        );

        return data;
      } catch (error) {
        console.error('Error assigning phone number to sessions:', error);
      }
    },
    setFoodCourtUserPhoneNumber({ commit }, phoneData) {
      commit('setFoodCourtUserPhoneNumber', phoneData);
      cookies.setCookie({
        name: 'foodCourtUserPhoneNumberCountryCode',
        value: phoneData.countryCallingCode,
        days: 1,
      });
      cookies.setCookie({
        name: 'foodCourtUserPhoneNumberNational',
        value: phoneData.nationalNumber,
        days: 1,
      });
    },
  },
  getters: {
    getFoodCourtDetails(state) {
      return state.foodCourt;
    },
    getFoodCourtId(_, getters) {
      return getters.getFoodCourtDetails.id;
    },
    getFoodCourtRestaurants(state) {
      return state.restaurants;
    },
    getFoodCourtRestaurantById: state => restaurantId => {
      return state.restaurants.find(
        restaurant => restaurant.id === restaurantId
      );
    },
    getFoodCourtLoading(state) {
      return state.foodCourtStatus === 'loading';
    },
    getRestaurantsLoading(state) {
      return state.restaurantsStatus === 'loading';
    },
    getFoodCourtOrdersLoading(state) {
      return state.foodCourtOrdersStatus === 'loading';
    },
    getFoodCourtSession(state) {
      return state.foodCourtSession;
    },
    getFoodCourtLanguage(state) {
      return state.language;
    },
    getFoodCourtLanguages(state) {
      return state.foodCourt.languages;
    },
    getFoodCourtDefaultLanguage(state) {
      return state.foodCourt.language;
    },
    getFoodCourtOrders(state) {
      return state.orders;
    },
    getFoodCourtHasOrders(state) {
      return state.orders.length > 0;
    },
    getFoodCourtOrderByUniqueOrderNumber: (_, getters) => uniqueOrderNumber => {
      const order = getters.getFoodCourtOrders.find(
        order => order.order.unique_order_number === uniqueOrderNumber
      );
      return order;
    },
    getRestaurantSession: state => restaurantId => {
      return get(state.restaurantSessionMap, restaurantId, null);
    },
    getFoodCourtSessionCookie(state) {
      return state.foodCourtSession;
    },
    getFoodCourtHash(state) {
      return state.foodCourtHash;
    },
    getFetchedLanguageByCode: state => language => {
      return state.fetchedLanguages.includes(language);
    },
    getIsSubscribedToPusherChannels(state) {
      return state.subscribedToPusherChannels;
    },
    getActiveOrdersCount(_, getters) {
      return getters.getFoodCourtOrders.reduce((acc, order) => {
        if (order.order_status !== 'closed') {
          acc += 1;
        }
        return acc;
      }, 0);
    },
    getRestaurantRushModeEnabled: (_, getters) => restaurantId => {
      const restaurants = getters.getFoodCourtRestaurants;
      const restaurant =
        restaurants.find(restaurant => restaurant.id === restaurantId) || {};

      return restaurant.order_status === 'rush_mode';
    },
    getShouldShowRushModeToast: (_, getters) => restaurantId => {
      const currentRestaurantId = getters.getCurrentRestaurantId;
      const restaurantRushModeEnabled = getters.getRestaurantRushModeEnabled(
        restaurantId
      );

      return restaurantId === currentRestaurantId && !restaurantRushModeEnabled;
    },
    getFoodCourtOrderOrderNumber: (_, getters) => uniqueOrderNumber => {
      const orders = getters.getFoodCourtOrders;
      return (
        orders.find(
          order => order.order.unique_order_number === uniqueOrderNumber
        )?.order || null
      );
    },
    isFoodCourtRestaurant: (_, getters) => restaurantId => {
      const restaurants = getters.getFoodCourtRestaurants;
      return restaurants.some(restaurant => restaurant.id === restaurantId);
    },
    getFoodCourtPaymentProviders: (_, getters) => {
      const foodCourt = getters.getFoodCourtDetails;
      return foodCourt.payment_providers || [];
    },
    getFoodCourtUserPhoneNumber(state) {
      const { countryCallingCode, nationalNumber } = state.foodCourtUserPhoneNumber;
      if (!countryCallingCode || !nationalNumber) {
        return null;
      }
      return `+${countryCallingCode}${nationalNumber}`;
    },
    getFoodCourtUserPhoneNumberParts(state) {
      return state.foodCourtUserPhoneNumber;
    },
    getFoodCourtAdditionalOrderFields(_, getters) {
      const foodCourt = getters.getFoodCourtDetails;
      return foodCourt.additional_order_fields || [];
    },
    getFoodCourtHasAdditionalOrderFields(_, getters) {
      const additionalOrderFields = getters.getFoodCourtAdditionalOrderFields;
      return additionalOrderFields.length > 0;
    },
    getIsFoodCourtSettingEnabled: state => featureName => {
      const settings = state.foodCourt.settings || []; // ["invoices", "sms-notifications", "disable-cart"]
      return settings.includes(featureName);
    },
  },
  modules: {
    cart: cartModule,
  },
};
